import { ICallForwardSettingValueResponse } from "@webex/component-adapter-interfaces/dist/esm/src";
// import { createCallSettingsClient } from "@webex/web-calling-sdk";
import { createCallSettingsClient } from '@webex/calling';
import config from '../../../../config';
import { LOGGER, Logger, formatError } from "../../../../packages/logger/src";
import { IBusinessNumberResponse, pickDisplayBusinessPhone } from "../../../adapter-interfaces/src/IBusinessNumber";
import { whenDeviceRegistered } from "./WebexInstanceService";

const ENTITLEMENT_WXC_AC_ADVANCED = "wxc-ac-advanced";
const DEVICE_FEATURE_CHECK_MAX_RETRIES = 3;
const DEVICE_FEATURE_CHECK_RETRY_DELAY_MS = 2000;

async function initCallSettingslList() {
  const booleanVal = config.WEBEX_CF_BOOLEAN_VAL;
  const callForwardBoolVal = (booleanVal == "true" ? true : false);
  const callSettingsClient = createCallSettingsClient((window as any)?.webex, {
    level: LOGGER.INFO,
  },
  callForwardBoolVal
  );
  const myUserDetails: IBusinessNumberResponse = await window?.webex?.people._getMe();
  const businessnum = pickDisplayBusinessPhone(myUserDetails?.phoneNumbers);
  const businessVal = businessnum?.value ? businessnum?.value.replace(/[+\s-]/g, "") : '';
  Logger.info(`business Num ${businessVal}`);
  const response = await callSettingsClient.getCallForwardAlwaysSetting(businessVal);
  const res = response as ICallForwardSettingValueResponse;
  return res as ICallForwardSettingValueResponse
}

/**
 * Fetches the user's call-forward setting from the Webex Calling SDK.
 *
 * Waits for both:
 *  1. `window.isWebexInitalized` — the SDK is constructed and `webex.ready` fired
 *  2. `whenDeviceRegistered(...)` — the device is registered against WDM/Mercury
 *
 * Step (2) matters because `WebexInstanceService.initWebex` deliberately flips
 * `isWebexInitalized = true` *before* `deviceRegister()` resolves (so unrelated
 * consumers like contacts init don't block). Without waiting for device
 * registration here, the very first call on login fires against a half-initialized
 * SDK and the XSI back end returns a permanent error (typically 400/403/404),
 * which the adapter's retry policy intentionally does NOT retry. The result is
 * an empty call-forward pill on first login that only recovers when the user
 * clicks "Call settings" and the polling loop kicks in. Mirrors the same wait
 * pattern already used by `getAttendantConsoleFeatuteToggle` and
 * `getCXEssentialsFeature` below.
 */
export async function getCallSetting(): Promise<ICallForwardSettingValueResponse> {
  if (!window?.isWebexInitalized) {
    await new Promise((f) => setTimeout(f, 1000));
    return getCallSetting();
  }
  try {
    await whenDeviceRegistered(15000);
  } catch (e) {
    Logger.info(`getCallSetting: device not registered in time, continuing with fetch: ${formatError(e)}`);
  }
  return initCallSettingslList();
}

/**
 * Initializes the Attendant Console feature toggle.
 * Checks device.features.entitlement.models for the required entitlement.
 * Retries when entitlement models are not yet populated so the menu appears once the device is ready.
 *
 * @returns {Promise<boolean>} - True when entitlement wxc-ac-advanced is present, false otherwise (after retries).
 */
async function initAttendantConsoleFeatureToggle(): Promise<boolean> {
  for (let attempt = 1; attempt <= DEVICE_FEATURE_CHECK_MAX_RETRIES; attempt++) {
    try {
      const entitlementModels = window.webex.internal.device.features.entitlement?.models ?? [];
      for (const model of entitlementModels) {
        if (model._values?.['key'] === ENTITLEMENT_WXC_AC_ADVANCED) {
          Logger.info(`Is attendant console Feature Enabled: true (attempt ${attempt})`);
          return true;
        }
      }

      if (attempt < DEVICE_FEATURE_CHECK_MAX_RETRIES) {
        Logger.info(`Attendant console entitlement not set yet (attempt ${attempt}), retrying in ${DEVICE_FEATURE_CHECK_RETRY_DELAY_MS}ms`);
        await new Promise((f) => setTimeout(f, DEVICE_FEATURE_CHECK_RETRY_DELAY_MS));
      } else {
        Logger.info(`Is attendant console Feature Enabled: false after ${attempt} attempts`);
        return false;
      }
    } catch (error) {
      Logger.error(
        `Is attendant console Feature Enabled: got error: '${formatError(error)}' (attempt ${attempt})`
      );
      if (attempt < DEVICE_FEATURE_CHECK_MAX_RETRIES) {
        await new Promise((f) => setTimeout(f, DEVICE_FEATURE_CHECK_RETRY_DELAY_MS));
      } else {
        return false;
      }
    }
  }
  return false;
}

/**
 * Retrieves the Attendant Console feature toggle.
 * Waits for Webex init and device registration before reading entitlements, so the result is accurate.
 *
 * @returns {Promise<boolean>} - A promise that resolves to a boolean indicating if the Attendant Console feature is enabled.
 */
export async function getAttendantConsoleFeatuteToggle(): Promise<boolean> {
  if (!window?.isWebexInitalized) {
    await new Promise((f) => setTimeout(f, 1000));
    return getAttendantConsoleFeatuteToggle();
  }
  try {
    await whenDeviceRegistered(15000);
  } catch (e) {
    Logger.info(`Attendant console: device not registered in time, continuing with check: ${formatError(e)}`);
  }
  return initAttendantConsoleFeatureToggle();
}


/**
 * Determines if Customer Assist (CX Essentials) is enabled from device productMode.
 * Uses the same logic for all environments (production and FedRAMP).
 * Retries when productMode is not yet populated so the menu appears once the device sets cxessential.
 *
 * @returns {Promise<boolean>} - True when productMode.cxessential is true, false otherwise (after retries).
 */
async function initCXEssentialsLicensedUser(): Promise<boolean> {
  for (let attempt = 1; attempt <= DEVICE_FEATURE_CHECK_MAX_RETRIES; attempt++) {
    try {
      const productMode = await window.webex.internal.device.productMode;

      if (productMode?.cxessential) {
        Logger.info(`Is CX-Essentials enabled for a user: true (attempt ${attempt})`);
        return true;
      }

      if (attempt < DEVICE_FEATURE_CHECK_MAX_RETRIES) {
        Logger.info(`CX-Essentials not set yet (attempt ${attempt}), retrying in ${DEVICE_FEATURE_CHECK_RETRY_DELAY_MS}ms`);
        await new Promise((f) => setTimeout(f, DEVICE_FEATURE_CHECK_RETRY_DELAY_MS));
      } else {
        Logger.info(`Is CX-Essentials enabled for a user: false after ${attempt} attempts`);
        return false;
      }
    } catch (error) {
      Logger.error(
        `Is CX-Essentials enabled for a user: got error: '${formatError(error)}' (attempt ${attempt})`
      );
      if (attempt < DEVICE_FEATURE_CHECK_MAX_RETRIES) {
        await new Promise((f) => setTimeout(f, DEVICE_FEATURE_CHECK_RETRY_DELAY_MS));
      } else {
        return false;
      }
    }
  }

  return false;
}

/**
 * Retrieves the CX-Essentials feature.
 * Waits for Webex init and device registration before reading productMode, so productMode.cxessential is available.
 *
 * @returns {Promise<boolean>} - A promise that resolves to a boolean indicating if the CXEssentials feature is enabled.
 */
export async function getCXEssentialsFeature(): Promise<boolean> {
  if (!window?.isWebexInitalized) {
    await new Promise((f) => setTimeout(f, 1000));
    return getCXEssentialsFeature();
  }
  try {
    await whenDeviceRegistered(15000);
  } catch (e) {
    Logger.info(`CX-Essentials: device not registered in time, continuing with check: ${formatError(e)}`);
  }
  return initCXEssentialsLicensedUser();
}
